import axios from "axios";

const NEW_API_URL =
  process.env.REACT_APP_NEST_REST_URL || "http://localhost:3000";

/**
 * Helper to execute GraphQL mutations against the NestJS backend.
 */
async function gqlMutation<T>(
  query: string,
  variables: Record<string, unknown>,
  token?: string
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await axios.post(
    `${NEW_API_URL}/graphql`,
    { query, variables },
    { headers }
  );

  if (response.data.errors) {
    const msg =
      response.data.errors[0]?.message || "Wystąpił błąd";
    throw new Error(msg);
  }

  return response.data.data;
}

/**
 * Helper to execute GraphQL queries against the NestJS backend.
 */
async function gqlQuery<T>(
  query: string,
  variables: Record<string, unknown>,
  token?: string
): Promise<T> {
  return gqlMutation<T>(query, variables, token);
}

// ── Auth Mutations ──

export interface AuthResponseData {
  accessToken: string;
  refreshToken: string;
  userId: number;
  username: string;
  isOnboarded: boolean;
  isEmailVerified: boolean;
}

export const registerUser = async (data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<AuthResponseData> => {
  const result = await gqlMutation<{ register: AuthResponseData }>(
    `mutation Register($input: RegisterInput!) {
      register(input: $input) {
        accessToken
        refreshToken
        userId
        username
        isOnboarded
        isEmailVerified
      }
    }`,
    { input: data }
  );
  return result.register;
};

export const googleLoginApi = async (
  idToken: string
): Promise<AuthResponseData> => {
  const result = await gqlMutation<{ googleLogin: AuthResponseData }>(
    `mutation GoogleLogin($input: GoogleLoginInput!) {
      googleLogin(input: $input) {
        accessToken
        refreshToken
        userId
        username
        isOnboarded
        isEmailVerified
      }
    }`,
    { input: { idToken } }
  );
  return result.googleLogin;
};

export const loginNest = async (data: {
  username: string;
  password: string;
}): Promise<AuthResponseData> => {
  const result = await gqlMutation<{ login: AuthResponseData }>(
    `mutation Login($input: LoginInput!) {
      login(input: $input) {
        accessToken
        refreshToken
        userId
        username
        isOnboarded
        isEmailVerified
      }
    }`,
    { input: data }
  );
  return result.login;
};

export const verifyEmailApi = async (
  token: string
): Promise<{ success: boolean }> => {
  const result = await gqlMutation<{
    verifyEmail: { success: boolean };
  }>(
    `mutation VerifyEmail($token: String!) {
      verifyEmail(token: $token) {
        success
      }
    }`,
    { token }
  );
  return result.verifyEmail;
};

export const refreshTokenApi = async (
  refreshToken: string
): Promise<{ accessToken: string; refreshToken: string }> => {
  const result = await gqlMutation<{
    refreshToken: { accessToken: string; refreshToken: string };
  }>(
    `mutation RefreshToken($refreshToken: String!) {
      refreshToken(refreshToken: $refreshToken) {
        accessToken
        refreshToken
      }
    }`,
    { refreshToken }
  );
  return result.refreshToken;
};

// ── Onboarding Mutations ──

export interface CreateCompanyData {
  companyId: number;
  companyName: string;
  joinCode: string;
}

export const createCompanyApi = async (
  name: string,
  token: string
): Promise<CreateCompanyData> => {
  const result = await gqlMutation<{
    createCompany: CreateCompanyData;
  }>(
    `mutation CreateCompany($name: String!) {
      createCompany(name: $name) {
        companyId
        companyName
        joinCode
      }
    }`,
    { name },
    token
  );
  return result.createCompany;
};

export interface JoinCompanyData {
  companyId: number;
  companyName: string;
}

export const joinCompanyByCodeApi = async (
  code: string,
  token: string
): Promise<JoinCompanyData> => {
  const result = await gqlMutation<{
    joinCompanyByCode: JoinCompanyData;
  }>(
    `mutation JoinCompanyByCode($code: String!) {
      joinCompanyByCode(code: $code) {
        companyId
        companyName
      }
    }`,
    { code },
    token
  );
  return result.joinCompanyByCode;
};

export interface JoinCodeData {
  code: string;
  expiresAt: string;
}

export const getCompanyJoinCodeApi = async (
  token: string
): Promise<JoinCodeData | null> => {
  const result = await gqlQuery<{
    companyJoinCode: JoinCodeData | null;
  }>(
    `query CompanyJoinCode {
      companyJoinCode {
        code
        expiresAt
      }
    }`,
    {},
    token
  );
  return result.companyJoinCode;
};
